import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

import { NavigationAction, SEARCH_PARAMS } from '../constants';

type NavigationActionHandlers = Partial<Record<NavigationAction, () => void>>;

type UseNavigationActionOptions = {
    /** Если false — эффект не выполняется. Используется для ожидания гидратации. */
    enabled?: boolean;
};

/**
 * Читает GET-параметр `action` из URL и вызывает соответствующий обработчик из переданного map.
 * Позволяет декларативно описывать реакции страницы на действия, инициированные при навигации.
 *
 * Использует паттерн «latest ref» — обработчики всегда актуальны без необходимости
 * мемоизировать их на стороне вызывающего кода.
 */
export const useNavigationAction = (handlers: NavigationActionHandlers, options: UseNavigationActionOptions = {}) => {
    const { enabled = true } = options;

    const [searchParams, setSearchParams] = useSearchParams();
    const handlersRef = useRef(handlers);

    handlersRef.current = handlers;

    useEffect(() => {
        if (!enabled) {
            return;
        }

        const action = searchParams.get(SEARCH_PARAMS.ACTION) as NavigationAction | null;

        if (!action || !(action in handlersRef.current)) {
            return;
        }

        const rafId = requestAnimationFrame(() => {
            handlersRef.current[action]?.();

            setSearchParams(
                prev => {
                    prev.delete(SEARCH_PARAMS.ACTION);

                    return prev;
                },
                { replace: true },
            );
        });

        return () => cancelAnimationFrame(rafId);
    }, [searchParams, enabled, setSearchParams]);
};
